import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import toast from "react-hot-toast";

import api from "services/api";
import moment from "moment";
import ImageCardWrapper from "components/UpStyled/ImageCardWrapper";
import colors from "assets/theme/base/colors";
import EditIcon from "@mui/icons-material/Edit";

import { nanoid } from "nanoid";
import Cropper from "react-easy-crop";

import UploadIcon from "@mui/icons-material/Upload";
import Slider from "@mui/material/Slider";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import {
  getBase64,
  dataURLtoFile,
  getBase64ImageFromUrl,
  uploadFileSizeLimit,
} from "utils/imageHelper";

const POST_NAME_MAX_LENGTH = 55;
const ASPECT_RATIO = 300 / 234;
const CROPPED_IMAGE_TYPE = "image/jpeg";

const SettingModal = React.forwardRef(
  ({ visible, handleClose, handleOk, post, setPost, edit, status }, ref) => {
    const [coverImage, setCoverImage] = useState();
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [editVisible, setEditVisible] = useState(false);

    const [croppedImage, setCroppedImage] = useState(null);
    const [postTitle, setPostTitle] = useState(post?.title);
    const [scale, setScale] = useState(1);
    const [imageRef, setImageRef] = useState(null);

    const [uploading, setUploading] = useState(false);

    const uploadRef = useRef(null);

    const navigate = useNavigate();

    // if (ref) {
    //   useImperativeHandle(
    //     ref,
    //     () => ({
    //       contentList,
    //     }),
    //     [setContentList]
    //   );
    // }

    useEffect(() => {
      setCroppedImage(post?.metadata?.coverImage?.url);
    }, []);

    const handleOnFileChange = async (e) => {
      const fileList = e?.target?.files;
      if (fileList.length > 0) {
        try {
          setUploading(true);
          const uri = await getBase64(fileList[0]);
          setCoverImage(uri);
          setUploading(false);
          toast.success("Upload successfully!");
        } catch (err) {
          setUploading(false);
          toast.error("Upload failed!");
        }
      }
    };

    const handleSaveSetting = async () => {
      let newPost = { ...post, status: status };
      const trimmedTitle = postTitle ? postTitle.trim() : "";
      if (!trimmedTitle) {
        return toast.error("A title is required for a post!");
      }
      if (!croppedImage) {
        return toast.error("A cover image is required for a post!");
      }

      if (!post?.metadata) {
        newPost["metadata"] = {
          settings: {
            backgroundColor: "#ffffff",
            mb: 40,
          },
          content: [],
        };
      }
      if (!post?.metadata?.content) {
        newPost["metadata"]["content"] = [];
      }

      if (!croppedImage || croppedImage == post?.metadata?.coverImage?.url) {
        //if no cropped image or doesn't change,then update title only

        newPost.title = trimmedTitle;

        handleClose();
      } else {
        try {
          setUploading(true);
          const fileName = post?.title || nanoid();
          const imageFile = dataURLtoFile(croppedImage, fileName);

          const { data } = await api.uploads.create([imageFile], {}, "/post");
          if (data?.uploads && data?.uploads.length > 0) {
            if (newPost?.metadata) {
              newPost.metadata.coverImage = {
                url: data?.uploads[0].url,
                name: fileName,
                type: CROPPED_IMAGE_TYPE,
              };
            }

            newPost.title = trimmedTitle;
            setUploading(false);
            handleClose();
          }
        } catch (err) {
          setUploading(false);
          console.log(err);
          return toast.error("Failed to save!");
        }
      }

      try {
        //do create
        if (status == "published") {
          newPost.metadata.published_at = moment();
        }

        const { data } = await api.post.create(newPost);

        toast.success(`Successfully save the post!`);
        navigate(`/my-posts?status=${status}`);
      } catch (err) {
        console.log(err);
        toast.error(`Failed to save the post!`);
      }
    };

    const renderExistedImages = () => {
      const content = post?.metadata?.content || [];
      const images = [];
      for (let i = 0; i < content.length; i++) {
        if (content[i]?.img?.url) {
          images.push(content[i].img);
        } else if (content[i].type == "grid") {
          const gridContent = content[i]?.content || [];

          for (let j = 0; j < gridContent.length; j++) {
            if (gridContent[j]?.img?.url) {
              images.push(gridContent[j].img);
            }
          }
        }
      }
      return (
        <div style={{ display: "flex", gap: "4px", flexWrap: "nowrap", overflow: "auto" }}>
          {images.map((item) => (
            <SuiBox
              key={item.img}
              style={{
                cursor: "pointer",
                width: 80,
                height: 80,
                overflow: "hidden",
                borderRadius: "8px",
                objectFit: "cover",
                objectPosition: "center",
              }}
              onClick={async (e) => {
                const uri = await getBase64ImageFromUrl(item.url);
                setCoverImage(uri);
              }}
              component="img"
              src={item.url}
              alt={item.name}
            ></SuiBox>
          ))}
        </div>
      );
    };

    const getCroppedImg = async (image, croppedAreaPixels) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const pixelRatio = window.devicePixelRatio;

      canvas.width = croppedAreaPixels.width * pixelRatio;
      canvas.height = croppedAreaPixels.height * pixelRatio;

      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      ctx.imageSmoothingQuality = "high";

      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );

      return canvas.toDataURL(CROPPED_IMAGE_TYPE);
    };

    //first parameter is percentage,second is pixels
    const onCropComplete = async (croppedArea, croppedAreaPixels) => {
      try {
        const croppedImageBase64 = await getCroppedImg(imageRef.current, croppedAreaPixels);
        setCroppedImage(croppedImageBase64);
      } catch (err) {
        console.log(err);
      }
    };

    return (
      <Dialog
        open={visible}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
        fullWidth={true}
        onClose={(e, reason) => {
          if (reason != "backdropClick") {
            handleClose();
          }
        }}
        sx={{ minWidth: 800 }}
      >
        <Backdrop sx={{ color: "#fff", zIndex: (theme) => 99999 }} open={uploading}>
          <CircularProgress color="inherit" />
        </Backdrop>
        <DialogTitle id="alert-dialog-title">
          {"Post Setting"}

          <IconButton
            aria-label="close"
            onClick={() => handleClose()}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <div style={{ overflow: "auto", maxHeight: "650px" }}>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <Grid container>
                <Grid item xs={12} sm={12} md={12}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography component="label" variant="caption" fontWeight="bold" required>
                        Post Title
                      </SuiTypography>
                    </SuiBox>
                    <SuiInput
                      componentsProps={{
                        input: {
                          maxlength: POST_NAME_MAX_LENGTH,
                        },
                      }}
                      required
                      value={postTitle}
                      onChange={(e) => {
                        setPostTitle(e.target.value);
                      }}
                      placeholder="Give your post a title"
                    />
                    <SuiBox display="flex" justifyContent="space-between">
                      <div></div>
                      <SuiTypography
                        variant="text"
                        color="secondary"
                        fontWeight="regular"
                        fontSize="14px"
                      >
                        {postTitle ? postTitle.length : 0}/{POST_NAME_MAX_LENGTH}
                      </SuiTypography>
                    </SuiBox>
                  </SuiBox>
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                    <SuiTypography component="label" variant="caption" fontWeight="bold" required>
                      Post Cover
                    </SuiTypography>
                  </SuiBox>
                  <SuiBox>
                    {croppedImage ? (
                      <img
                        src={croppedImage}
                        alt="Cropped Image"
                        style={{
                          width: 300,
                          height: "auto",
                          boxShadow: "0 0 24px rgba(0, 0, 0, 0.3)",
                        }}
                      />
                    ) : (
                      <SuiBox sx={{ width: 300 }}>
                        <SuiBox
                          sx={{
                            width: 300,
                            height: 234,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexDirection: "column",
                            border: `dashed 1px ${colors.text.main}`,
                          }}
                        >
                          <SuiTypography
                            variant="body1"
                            color="text"
                            sx={{
                              cursor: "pointer",
                            }}
                            onClick={() => setEditVisible(true)}
                          >
                            Upload or choose a cover image
                          </SuiTypography>
                        </SuiBox>
                      </SuiBox>
                    )}
                    <SuiTypography
                      variant="body1"
                      color="info"
                      sx={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        width: 300,
                        gap: 0.5,
                        mt: 1,
                      }}
                      onClick={() => setEditVisible(true)}
                    >
                      <EditIcon /> Edit
                    </SuiTypography>
                  </SuiBox>
                </Grid>
                {editVisible ? (
                  <Grid item xs={12} sm={6} md={6}>
                    <SuiBox>
                      <SuiBox sx={{ minWidth: 300, width: 300 }}>
                        <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                          <SuiTypography component="label" variant="caption" fontWeight="bold">
                            Crop the cover
                          </SuiTypography>
                        </SuiBox>
                        <SuiBox
                          sx={{
                            minWidth: 300,
                            width: 300,
                          }}
                        >
                          {coverImage ? (
                            <div>
                              <div
                                style={{
                                  minWidth: 300,
                                  width: 300,
                                  height: 234,
                                  position: "relative",
                                }}
                              >
                                <Cropper
                                  image={coverImage}
                                  crop={crop}
                                  zoom={scale}
                                  aspect={ASPECT_RATIO}
                                  onCropChange={setCrop}
                                  onCropComplete={onCropComplete}
                                  onZoomChange={setScale}
                                  maxZoom={5}
                                  minZoom={1}
                                  setImageRef={(ref) => setImageRef(ref)}
                                />
                              </div>
                              <SuiBox sx={{ maxWidth: 200, mx: "auto" }}>
                                <SuiBox
                                  gap={1}
                                  display="flex"
                                  sx={{ mb: 1 }}
                                  alignItems="center"
                                  justifyContent={"center"}
                                >
                                  <ZoomOutIcon fontSize="medium" color={"text"} />
                                  <Slider
                                    aria-label="Volume"
                                    value={scale}
                                    onChange={(e, value) => setScale(value)}
                                    max={5}
                                    min={1}
                                    step={0.1}
                                  />
                                  <ZoomInIcon fontSize="medium" color={"text"} />
                                </SuiBox>
                              </SuiBox>
                              <SuiTypography
                                variant="body1"
                                color="info"
                                sx={{
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 0.5,
                                }}
                                onClick={() => uploadRef?.current?.click()}
                              >
                                <UploadIcon /> Upload Image
                              </SuiTypography>
                            </div>
                          ) : (
                            <SuiBox
                              sx={{
                                width: "100%",
                                height: 234,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexDirection: "column",
                                border: `dashed 1px ${colors.text.main}`,
                              }}
                            >
                              <SuiButton
                                variant="contained"
                                color="info"
                                circular
                                size="small"
                                onClick={() => uploadRef?.current?.click()}
                              >
                                Upload Image
                              </SuiButton>
                              <SuiTypography color="text" variant="body2" sx={{ mt: 1 }}>
                                Upload your cover image
                              </SuiTypography>
                            </SuiBox>
                          )}{" "}
                        </SuiBox>
                      </SuiBox>
                      <SuiBox sx={{ flexGrow: 1, width: "100%", mt: 2 }}>
                        {renderExistedImages()}
                      </SuiBox>
                    </SuiBox>
                  </Grid>
                ) : null}
              </Grid>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <SuiButton variant="gradient" size="medium" onClick={handleClose} color="light">
              Cancel
            </SuiButton>
            <SuiButton size="medium" variant="gradient" color="dark" onClick={handleSaveSetting}>
              Save
            </SuiButton>
          </DialogActions>
          <input
            type="file"
            id="imgupload"
            style={{ display: "none" }}
            ref={uploadRef}
            accept="image/*"
            onChange={handleOnFileChange}
          />
        </div>
      </Dialog>
    );
  }
);

export default SettingModal;
